/**
 * This file contains shared business logic for private deal detection and related helpers.
 *
 * The extraction of `isPrivateDeal` and related logic to this file is necessary to avoid circular dependencies
 * between `selectors.ts` and `privateDeals.ts`. Both files require access to this logic, but importing one from the other
 * directly would create a circular import.
 */

import {head, includes} from 'ramda'

import {
  FRIENDS_AND_FAMILY_CUG_TYPE,
  isWeekendPromoOffer,
  OFFLINE_CUG_TYPE,
  PLUS_CUG_TYPE,
  RESTRICTED_CUG_TYPE,
  SENSITIVE_CUG_TYPE,
  SIGNED_IN_CUG_TYPE
} from '../../offer/business/privateDeals'
import {GenericOffer, Offer} from '../../offer/types/offer'
import {SearchOffer} from '../../offer/types/SearchOffer'

/**
 * This method must only be used when there is not support to getIsLockedDeal selector.
 * Determines if an offer is a Closed User Group (CUG) offer (private deal).
 * @param offer - The offer to be checked
 * @param shouldSeeOffersUnlocked - Whether the user should see CUG offers as unlocked
 * @returns Whether the offer is a private deal
 */
export const isPrivateDeal = (
  offer?: Partial<SearchOffer> | Partial<Offer> | GenericOffer | null,
  shouldSeeOffersUnlocked?: boolean
) => {
  if (!offer) return false

  if (isWeekendPromoOffer(offer)) {
    return shouldSeeOffersUnlocked
  }

  const accessTier =
    (offer as SearchOffer)?.accessTier ?? head((offer as Offer)?.cug || [])

  return includes(accessTier, [
    OFFLINE_CUG_TYPE,
    SIGNED_IN_CUG_TYPE,
    SENSITIVE_CUG_TYPE,
    PLUS_CUG_TYPE,
    FRIENDS_AND_FAMILY_CUG_TYPE,
    RESTRICTED_CUG_TYPE
  ])
}
